import {
  EnvironmentProviders,
  Provider,
  inject,
  provideAppInitializer
} from '@angular/core';
import {
  createEkoLanguagePersistHandler,
  createEkoLanguageProfileReader,
  createEkoThemePersistHandler,
  createEkoThemeProfileReader,
  EKO_LANGUAGE_PERSIST_HANDLER,
  EKO_LANGUAGE_PROFILE_READER,
  EKO_THEME_PERSIST_HANDLER,
  EKO_THEME_PROFILE_READER,
  EkoLanguageService,
  EkoThemeService,
  invalidateKeycloakProfileCache
} from '@ekowitryna/ui';
import { AccountService } from '@mobiletract/account-service-angular-client';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';

const KEYCLOAK_INIT_TIMEOUT_MS = 12000;
const KEYCLOAK_INIT_POLL_MS = 100;
const AVAILABLE_LANGUAGES = ['pl', 'en', 'de', 'es', 'uk', 'fr'] as const;

export function provideEkoPreferencesSync(): Array<Provider | EnvironmentProviders> {
  return [
    {
      provide: EKO_THEME_PROFILE_READER,
      useFactory: (keycloak: KeycloakService) =>
        createEkoThemeProfileReader(async () => {
          if (!(await keycloak.isLoggedIn())) {
            return undefined;
          }
          invalidateKeycloakProfileCache(keycloak.getKeycloakInstance());
          return keycloak.loadUserProfile();
        }),
      deps: [KeycloakService]
    },
    {
      provide: EKO_THEME_PERSIST_HANDLER,
      useFactory: (accountService: AccountService) =>
        createEkoThemePersistHandler(async (theme) => {
          await firstValueFrom(accountService.setTheme({ theme }));
        }),
      deps: [AccountService]
    },
    {
      provide: EKO_LANGUAGE_PROFILE_READER,
      useFactory: (keycloak: KeycloakService) =>
        createEkoLanguageProfileReader(async () => {
          if (!(await keycloak.isLoggedIn())) {
            return undefined;
          }
          invalidateKeycloakProfileCache(keycloak.getKeycloakInstance());
          return keycloak.loadUserProfile();
        }),
      deps: [KeycloakService]
    },
    {
      provide: EKO_LANGUAGE_PERSIST_HANDLER,
      useFactory: (accountService: AccountService, keycloak: KeycloakService) =>
        createEkoLanguagePersistHandler(async (locale: string) => {
          if (!(await keycloak.isLoggedIn())) {
            return;
          }
          await firstValueFrom(accountService.setLocale({ locale }));
        }),
      deps: [AccountService, KeycloakService]
    },
    provideAppInitializer(async () => {
      const keycloak = inject(KeycloakService);
      const theme = inject(EkoThemeService);
      const language = inject(EkoLanguageService);
      await waitForKeycloakInitialization(keycloak);

      if (await keycloak.isLoggedIn()) {
        return;
      }

      theme.initFromStorageOrSystem();
      language.initializeLanguage([...AVAILABLE_LANGUAGES]);
    })
  ];
}

async function waitForKeycloakInitialization(keycloak: KeycloakService): Promise<void> {
  const instance = keycloak.getKeycloakInstance();
  if (instance.didInitialize) {
    return;
  }

  const startedAt = Date.now();
  while (!instance.didInitialize && Date.now() - startedAt < KEYCLOAK_INIT_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, KEYCLOAK_INIT_POLL_MS));
  }
}
